import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { toHashRouterUrl } from 'components/Pages/StudyReport/studyReportViewerLinks';
import { SlidePreviewCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { SlideInferenceResults } from 'interfaces/calculateFeatures';
import { Job, JobArchiveState, JobStatus, JobType } from 'interfaces/job';
import { Slide } from 'interfaces/slide';
import { filter, flatMap, fromPairs, isArray, isEmpty, isString, keyBy, map, reduce, uniq, values } from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useClipboard } from 'use-clipboard-copy';
import { downloadDataAsCSV } from 'utils/exports';
import { getJobQueryKey } from 'utils/queryHooks/jobs/useJob';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';
import { useUiSettings } from 'utils/queryHooks/uiConstantsHooks';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { getUrlToDatasetTabForSlideIds } from 'utils/useNavigationToDatasetTab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { JobDetailsButton } from './JobDetailsLink';
import { JobExperimentResultsSection } from './JobExperimentResultsSection';
import { getJobStatusDisplayLabel, JobStatusChip } from './JobStatusChip';
import { getJobOutcomeSummary } from './jobOutcomeSummary';
import { getJobTypeDisplayLabel } from './jobTypeLabels';

const SKIP = 'skip';

interface SubtaskDetailsProps {
  job: Partial<Job>;
  isLoading?: boolean;
}

interface SlideRow {
  key: string;
  slideIds: string[];
  label: string;
  caseId?: string;
}

const getJobManifestSource = (job: Partial<Job>): unknown => {
  if (job.type === JobType.PrepareCustomerResults) {
    return undefined;
  }

  if (!isEmpty(job.manifest)) {
    return job.manifest;
  }

  const rawJob = job as Partial<Job> & {
    params?: Record<string, unknown>;
    results?: Record<string, unknown>;
  };
  const params = rawJob.params || {};
  if (!isEmpty(params.manifest)) {
    return params.manifest;
  }

  const directSlideId = params.slide_id || params.slideId || rawJob.results?.slide_id;
  if (isString(directSlideId)) {
    return [directSlideId];
  }

  const polygons = params.secondary_analysis_polygons || params.secondaryAnalysisPolygons;
  return polygons && typeof polygons === 'object'
    ? flatMap(values(polygons), (casePolygons) =>
        casePolygons && typeof casePolygons === 'object' ? Object.keys(casePolygons) : []
      )
    : undefined;
};

const getSlideRegistrationKey = (slidePair: { slide_id1: string; slide_id2: string }) =>
  `${slidePair.slide_id1}:${slidePair.slide_id2}`;

const getSlideRows = (manifest: unknown, jobType?: JobType): SlideRow[] => {
  if (jobType === JobType.SlideRegistrations) {
    return flatMap(isArray(manifest) ? manifest : [], (entry) => {
      if (!entry || typeof entry !== 'object' || !('slide_id1' in entry) || !('slide_id2' in entry)) {
        return [];
      }

      const slidePair = entry as { slide_id1: string; slide_id2: string };
      return [
        {
          key: getSlideRegistrationKey(slidePair),
          slideIds: [slidePair.slide_id1, slidePair.slide_id2],
          label: 'Registration',
        },
      ];
    });
  }

  if (
    jobType === JobType.PostProcessingAndCalculateFeatures &&
    manifest &&
    typeof manifest === 'object' &&
    !isArray(manifest)
  ) {
    return flatMap(Object.entries(manifest), ([caseId, caseSlides]) =>
      map(filter(isArray(caseSlides) ? caseSlides : [], isString), (slideId) => ({
        key: slideId,
        slideIds: [slideId],
        label: slideId,
        caseId,
      }))
    );
  }

  return flatMap(isArray(manifest) ? manifest : [], (entry) => {
    if (isString(entry)) {
      return [{ key: entry, slideIds: [entry], label: entry }];
    }
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    if ('slide_id' in entry && isString(entry.slide_id)) {
      return [{ key: entry.slide_id, slideIds: [entry.slide_id], label: entry.slide_id }];
    }
    if (!('case_id' in entry) || !('slides' in entry)) {
      return [];
    }

    const manifestCase = entry as { case_id: number | string; slides: Record<string, unknown> };
    return map(filter(values(manifestCase.slides), isString), (slideId) => ({
      key: slideId,
      slideIds: [slideId],
      label: slideId,
      ...(String(manifestCase.case_id) !== '0' && { caseId: String(manifestCase.case_id) }),
    }));
  });
};

const getChildJobSlideKeys = (childJob: Partial<Job>, parentType?: JobType): string[] => {
  if (childJob.type === JobType.PrepareCustomerResults || parentType === JobType.PrepareCustomerResults) {
    return [];
  }

  const manifest = childJob.manifest;
  if (childJob.type === JobType.SlideRegistrations || parentType === JobType.SlideRegistrations) {
    return flatMap(isArray(manifest) ? manifest : [], (entry) =>
      entry && typeof entry === 'object' && 'slide_id1' in entry && 'slide_id2' in entry
        ? [`${entry.slide_id1}:${entry.slide_id2}`]
        : []
    );
  }

  if (
    childJob.type === JobType.PostProcessingAndCalculateFeatures ||
    parentType === JobType.PostProcessingAndCalculateFeatures
  ) {
    const entries = isArray(manifest) ? manifest : manifest && typeof manifest === 'object' ? [manifest] : [];
    return flatMap(entries, (entry) => {
      if (isString(entry)) {
        return [entry];
      }
      return entry && typeof entry === 'object'
        ? flatMap(values(entry), (caseSlides) => (isArray(caseSlides) ? filter(caseSlides, isString) : []))
        : [];
    });
  }

  return map(getSlideRows(manifest, childJob.type), 'key');
};

const getSlideStageSummary = (slideJobs: Partial<Job>[], statusLabel: (status?: string) => string) => {
  if (slideJobs.length === 0 || slideJobs.some((slideJob) => !slideJob.status)) {
    return undefined;
  }

  if (slideJobs.length === 1) {
    return { status: slideJobs[0].status, label: statusLabel(slideJobs[0].status) };
  }

  const childStatusCounts = reduce(
    slideJobs,
    (counts: Record<string, number>, slideJob) => {
      const status = slideJob.status as string;
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    },
    {}
  );
  const summary = getJobOutcomeSummary({ childStatusCounts });
  const completedCount = childStatusCounts[JobStatus.Completed] || 0;
  const label =
    completedCount === slideJobs.length ? 'Stages complete' : `${completedCount}/${slideJobs.length} stages complete`;

  return {
    status: summary.displayStatus,
    label,
  };
};

const SubtaskJobRow: React.FC<{
  job: Partial<Job>;
  parentJobId?: string;
  parentJobType?: JobType;
  parentJobName?: string;
  isLoading?: boolean;
  resultSlideId?: string;
  statusLabel: (status?: string) => string;
  hideJobLabel?: boolean;
  hideJobStatus?: boolean;
}> = ({
  job,
  parentJobId,
  parentJobType,
  parentJobName,
  isLoading = false,
  resultSlideId,
  statusLabel,
  hideJobLabel = false,
  hideJobStatus = false,
}) => {
  const [resultsExpanded, setResultsExpanded] = React.useState(false);
  const [failureDetailsExpanded, setFailureDetailsExpanded] = React.useState(false);
  const failureClipboard = useClipboard({ copiedTimeout: 1000 });
  const hasResults =
    Boolean(job.id && job.orchestrationId && job.status === JobStatus.Completed) &&
    parentJobType !== JobType.PrepareCustomerResults &&
    job.type !== JobType.PrepareCustomerResults;
  const jobLabel = job.type
    ? getJobTypeDisplayLabel(job.type)
    : job.name && job.name !== parentJobName
    ? job.name
    : 'Job';
  const isFailureStatus = job.status === JobStatus.Failed || job.status === JobStatus.FailedToStart;
  const failureMessage = job.statusMessage?.trim();
  const hasFailureDetails = isFailureStatus && Boolean(failureMessage || job.externalTaskLink);
  const failureJobLabel = job.name || job.id || jobLabel;

  return (
    <Grid item container alignItems="center" spacing={1} sx={{ pl: 2, py: 0.75 }}>
      <Grid item xs sx={{ minWidth: 0 }}>
        {hideJobLabel ? null : isLoading && !job.type ? (
          <Skeleton variant="text" width="70%" />
        ) : (
          <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
            {jobLabel}
          </Typography>
        )}
      </Grid>
      {!hideJobStatus && job.status ? (
        <Grid item>
          <JobStatusChip status={job.status} label={statusLabel(job.status)} />
        </Grid>
      ) : !hideJobStatus && isLoading ? (
        <Grid item>
          <Skeleton variant="rounded" width={86} height={24} />
        </Grid>
      ) : null}
      {job.deletedAt && (
        <Grid item>
          <Chip size="small" variant="outlined" label="Archived" aria-label="Archived job" />
        </Grid>
      )}
      {hasFailureDetails && (
        <Grid item>
          <Button
            size="small"
            color="inherit"
            endIcon={failureDetailsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            aria-label={`${failureDetailsExpanded ? 'Hide' : 'Show'} failure details for ${failureJobLabel}`}
            aria-expanded={failureDetailsExpanded}
            onClick={() => setFailureDetailsExpanded((expanded) => !expanded)}
            sx={{ textTransform: 'none' }}
          >
            Failure details
          </Button>
        </Grid>
      )}
      {hasResults && job.id && (
        <Grid item>
          <Button
            size="small"
            color="inherit"
            endIcon={resultsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            aria-label={`${resultsExpanded ? 'Hide' : 'Show'} results for ${jobLabel}`}
            aria-expanded={resultsExpanded}
            onClick={() => setResultsExpanded((expanded) => !expanded)}
            sx={{ textTransform: 'none' }}
          >
            Results
          </Button>
        </Grid>
      )}
      {job.id && (
        <Grid item>
          <JobDetailsButton
            jobId={job.id}
            returnToParentJobId={parentJobId}
            ariaLabel={`Open job ${job.id}`}
            title={job.id}
          >
            Open job
          </JobDetailsButton>
        </Grid>
      )}
      {hasFailureDetails && (
        <Grid item xs={12}>
          <Collapse in={failureDetailsExpanded} unmountOnExit>
            <Stack spacing={0.75} sx={{ pl: 4, pt: 0.5 }}>
              {failureMessage && (
                <Typography variant="body2" color="error" sx={{ overflowWrap: 'anywhere' }}>
                  {failureMessage}
                </Typography>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                {failureMessage && (
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    aria-label={`Copy failure message for ${failureJobLabel}`}
                    onClick={() => failureClipboard.copy(failureMessage)}
                  >
                    Copy error
                  </Button>
                )}
                {job.externalTaskLink && (
                  <Link href={job.externalTaskLink} target="_blank" rel="noopener noreferrer" underline="hover">
                    External task link
                  </Link>
                )}
              </Stack>
            </Stack>
          </Collapse>
        </Grid>
      )}
      {hasResults && job.id && (
        <Grid item xs={12}>
          <Collapse in={resultsExpanded} unmountOnExit>
            <JobExperimentResultsSection jobId={job.id} showSummary={false} slideId={resultSlideId} />
          </Collapse>
        </Grid>
      )}
    </Grid>
  );
};

const SlideResultsDisclosure: React.FC<{ jobId: string; slideId?: string }> = ({ jobId, slideId }) => {
  const [resultsExpanded, setResultsExpanded] = React.useState(false);

  return (
    <Grid item xs={12} sx={{ pl: 2 }}>
      <Button
        size="small"
        color="inherit"
        variant="text"
        startIcon={resultsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        aria-expanded={resultsExpanded}
        onClick={() => setResultsExpanded((expanded) => !expanded)}
      >
        Results for this slide
      </Button>
      {resultsExpanded && <JobExperimentResultsSection jobId={jobId} showSummary={false} slideId={slideId} />}
    </Grid>
  );
};

const COLLAPSE_SLIDES_ABOVE = 3;

const SlideGroup: React.FC<{
  slideRow: SlideRow;
  slideDetailsById: Record<string, SlideInferenceResults>;
  labId?: string;
  studyId?: string;
  statusSummary?: { status?: string; label: string };
  isLoadingStatus: boolean;
  children: React.ReactNode;
}> = ({ slideRow, slideDetailsById, labId, studyId, statusSummary, isLoadingStatus, children }) => {
  const { getUrlToSlidePage } = useNavigationToViewerPage();
  const firstDetails = slideDetailsById[slideRow.slideIds[0]];
  const caseLabel = firstDetails?.caseLabel || (slideRow.caseId ? `Case ${slideRow.caseId}` : undefined);
  const slides: Slide[] = map(slideRow.slideIds, (slideId) => ({
    id: slideId,
    labId,
    stainingType: slideDetailsById[slideId]?.stainingType,
    originalFileName: slideDetailsById[slideId]?.originalFileName,
  }));

  return (
    <Grid item container direction="column" spacing={0.5}>
      <Grid item container alignItems="center" wrap="nowrap" columnSpacing={1}>
        <Grid item xs sx={{ minWidth: 0 }}>
          <SlidePreviewCell
            slides={slides}
            labId={labId}
            caseStudyId={studyId}
            slidesMode
            summaryContent={
              <Stack sx={{ minWidth: 0 }}>
                {caseLabel && (
                  <Typography variant="caption" color="text.secondary" noWrap title={caseLabel}>
                    {caseLabel}
                  </Typography>
                )}
                {map(slideRow.slideIds, (slideId) => (
                  <Link
                    key={slideId}
                    to={getUrlToSlidePage({ slideId, labId, caseStudyId: studyId })}
                    target="_blank"
                    rel="noopener noreferrer"
                    component={RouterLink}
                    underline="hover"
                    variant="body2"
                    fontWeight={600}
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {slideId}
                  </Link>
                ))}
              </Stack>
            }
          />
        </Grid>
        <Grid item sx={{ flexShrink: 0 }}>
          {isLoadingStatus ? (
            <Skeleton variant="rounded" width={104} height={24} />
          ) : (
            statusSummary && <JobStatusChip status={statusSummary.status} label={statusSummary.label} />
          )}
        </Grid>
      </Grid>
      {children}
      <Grid item>
        <Divider />
      </Grid>
    </Grid>
  );
};

export const SubtaskDetails: React.FC<SubtaskDetailsProps> = ({ job, isLoading = false }) => {
  const { labId } = useCurrentLabId();
  const queryClient = useQueryClient();
  const slideClipboard = useClipboard({ copiedTimeout: 1000 });
  const [slideSearch, setSlideSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>();
  const childJobs = (job.children ?? []) as Job[];
  const childJobIds = map(childJobs, 'id').filter(Boolean) as string[];
  const {
    data: childJobsData,
    isFetching: isFetchingChildJobs,
    isError: isErrorChildJobs,
  } = useJobs({
    enabled: childJobIds.length > 0,
    noInitialData: true,
    page: 1,
    pageSize: Math.max(childJobIds.length, 1),
    childTasksOnly: true,
    additionalFilters: { archiveState: JobArchiveState.All, jobIds: childJobIds },
  });
  const jobsById = keyBy(childJobsData?.jobs || [], 'id');
  const hydratedChildJobs = map(childJobs, (childJob) => (childJob.id ? jobsById[childJob.id] || childJob : childJob));

  React.useEffect(() => {
    map(childJobsData?.jobs || [], (childJob) => {
      if (childJob.id) {
        queryClient.setQueryData(getJobQueryKey(childJob.id), (existingJob?: Job) =>
          existingJob ? { ...existingJob, ...childJob } : childJob
        );
      }
    });
  }, [childJobsData?.jobs, queryClient]);

  const manifest = getJobManifestSource(job);
  const slideRows = getSlideRows(manifest, job.type);
  const manifestSlideIds = uniq(flatMap(slideRows, 'slideIds'));
  const {
    data: slideDetails,
    isLoading: isLoadingSlideDetails,
    isError: isSlideDetailsError,
  } = useQuery({
    queryKey: ['jobManifestSlideDetails', job.studyId, labId, manifestSlideIds],
    queryFn: ({ signal }) =>
      getSlidesDataForArtifactsResults(
        { studyId: job.studyId, labId, slidesMode: true, slideIdsToInclude: manifestSlideIds },
        signal
      ),
    enabled: Boolean(job.studyId) && manifestSlideIds.length > 0,
    staleTime: Infinity,
    retry: false,
  });
  const slideDetailsById = keyBy(slideDetails || [], 'slideId');
  const unscopedChildJobs = filter(
    hydratedChildJobs,
    (childJob) => getChildJobSlideKeys(childJob, job.type).length === 0
  );
  const childJobsWithoutSlide = filter(
    hydratedChildJobs,
    (childJob) =>
      !slideRows.some((slideRow) =>
        getChildJobSlideKeys(childJob, job.type).some((key) => slideRow.key === key || slideRow.slideIds.includes(key))
      ) &&
      !(job.isParentJob === true && slideRows.length === 1 && getChildJobSlideKeys(childJob, job.type).length === 0)
  );
  const jobStatusLabels = fromPairs(
    map(useUiSettings().uiSettings?.enumDisplayNames?.['jobStatus'], ({ value, label }) => [value, label]) || []
  );
  jobStatusLabels[SKIP] = 'Skipped';
  const getStatusLabel = (status?: string) => getJobStatusDisplayLabel(status, jobStatusLabels);
  const { getUrlToSlidePage } = useNavigationToViewerPage();

  const jobsForSlide = (slideRow: SlideRow) => {
    const matchingJobs = filter(hydratedChildJobs, (childJob) => {
      const childKeys = getChildJobSlideKeys(childJob, job.type);
      return childKeys.some((key) => slideRow.key === key || slideRow.slideIds.includes(key));
    });
    if (job.isParentJob === true && slideRows.length === 1) {
      return [...matchingJobs, ...unscopedChildJobs];
    }
    if (!isEmpty(matchingJobs)) {
      return matchingJobs;
    }
    return job.isParentJob === false && !job.parentId ? [job] : [];
  };

  const childTaskTypes = uniq(map(hydratedChildJobs, 'type').filter(Boolean) as JobType[]);
  const isOrchestrationBatch =
    job.type === JobType.NucleaiExecutor ||
    String(job.type) === 'nucleai_orchestration_builder' ||
    childTaskTypes.length > 1 ||
    hydratedChildJobs.some((childJob) => childJob.isParentJob === true);
  const simpleBatchTaskLabel =
    job.isParentJob === true && hydratedChildJobs.length > 0 && !isOrchestrationBatch && childTaskTypes.length === 1
      ? getJobTypeDisplayLabel(childTaskTypes[0])
      : undefined;
  const availableStatuses = uniq(map(hydratedChildJobs, 'status').filter(Boolean) as string[]);
  const normalizedSlideSearch = slideSearch.trim().toLowerCase();
  const slideMatchesSearch = (slideRow: SlideRow) => {
    if (!normalizedSlideSearch) {
      return true;
    }

    const searchableValues = [
      slideRow.caseId,
      ...slideRow.slideIds,
      ...flatMap(slideRow.slideIds, (slideId) => [
        slideDetailsById[slideId]?.caseId,
        slideDetailsById[slideId]?.caseLabel,
      ]),
    ];
    return searchableValues.some((value) =>
      String(value || '')
        .toLowerCase()
        .includes(normalizedSlideSearch)
    );
  };
  const jobsForVisibleSlide = (slideRow: SlideRow) =>
    filter(jobsForSlide(slideRow), (slideJob) => !statusFilter || slideJob.status === statusFilter);
  const visibleSlideRows = filter(
    slideRows,
    (slideRow) =>
      slideMatchesSearch(slideRow) &&
      (!statusFilter || jobsForSlide(slideRow).some((slideJob) => slideJob.status === statusFilter))
  );
  const visibleSlideIds = uniq(flatMap(visibleSlideRows, 'slideIds'));

  const slideIdsByStatus = reduce(
    slideRows,
    (statusMap: Record<string, string[]>, slideRow) => {
      const statuses = uniq(map(jobsForSlide(slideRow), 'status').filter(Boolean) as string[]);
      statuses.forEach((status) => {
        statusMap[status] = uniq([...(statusMap[status] || []), ...slideRow.slideIds]);
      });
      return statusMap;
    },
    {}
  );
  const failedSlideIds = slideIdsByStatus[JobStatus.Failed] || [];
  const completedSlideCount = filter(manifestSlideIds, (slideId) => {
    const slideJobs = flatMap(
      filter(slideRows, (slideRow) => slideRow.slideIds.includes(slideId)),
      jobsForSlide
    );
    return slideJobs.length > 0 && slideJobs.every((slideJob) => slideJob.status === JobStatus.Completed);
  }).length;
  const visibleChildJobsWithoutSlide = filter(
    childJobsWithoutSlide,
    (childJob) => !normalizedSlideSearch && (!statusFilter || childJob.status === statusFilter)
  );
  const searchMatchesSlideId = slideRows.some((slideRow) =>
    slideRow.slideIds.some((slideId) => slideId.toLowerCase().includes(normalizedSlideSearch))
  );
  const isWaitingForCaseSearch = Boolean(
    normalizedSlideSearch && isLoadingSlideDetails && !searchMatchesSlideId && visibleSlideRows.length === 0
  );

  const handleExportSlidesCsv = () => {
    const csvRows: string[][] = [
      ['Slide Id', 'Job', 'Status', 'App Link'],
      ...flatMap(slideRows, (slideRow) =>
        flatMap(jobsForSlide(slideRow), (slideJob) =>
          map(slideRow.slideIds, (slideId) => [
            slideId,
            slideJob.name || getJobTypeDisplayLabel(slideJob.type as JobType) || 'Job',
            getStatusLabel(slideJob.status),
            `${window.location.origin}${toHashRouterUrl(
              getUrlToSlidePage({ slideId, labId, caseStudyId: job.studyId })
            )}`,
          ])
        )
      ),
    ];
    downloadDataAsCSV(csvRows, `job-${job.id || 'subtasks'}-slides`);
  };

  const renderSlideRow = (slideRow: SlideRow) => {
    const slideJobs = jobsForVisibleSlide(slideRow);
    const slideStageSummary =
      job.isParentJob === true ? getSlideStageSummary(jobsForSlide(slideRow), getStatusLabel) : undefined;
    return (
      <SlideGroup
        key={slideRow.key}
        slideRow={slideRow}
        slideDetailsById={slideDetailsById}
        labId={labId}
        studyId={job.studyId}
        statusSummary={slideStageSummary}
        isLoadingStatus={isFetchingChildJobs}
      >
        {!isEmpty(slideJobs) ? (
          map(slideJobs, (slideJob) => (
            <SubtaskJobRow
              key={`${slideRow.key}-${slideJob.id || 'job'}`}
              job={slideJob}
              parentJobId={job.id}
              parentJobType={job.type}
              parentJobName={job.name}
              isLoading={isFetchingChildJobs}
              resultSlideId={slideRow.slideIds.length === 1 ? slideRow.slideIds[0] : undefined}
              statusLabel={getStatusLabel}
              hideJobLabel={Boolean(simpleBatchTaskLabel)}
              hideJobStatus={Boolean(simpleBatchTaskLabel) && slideJobs.length === 1}
            />
          ))
        ) : isFetchingChildJobs ? (
          <Skeleton variant="text" width="70%" />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
            No job recorded for this slide.
          </Typography>
        )}
        {job.isParentJob === true &&
          job.status === JobStatus.Completed &&
          slideRows.length === 1 &&
          job.id &&
          job.orchestrationId && <SlideResultsDisclosure jobId={job.id} slideId={slideRow.slideIds[0]} />}
      </SlideGroup>
    );
  };

  return (
    <Grid item container component="section" direction="column" spacing={1.5}>
      {isLoading ? (
        <Grid item>
          <Skeleton variant="rectangular" height={96} />
        </Grid>
      ) : (
        <>
          <Grid item container alignItems="center" justifyContent="space-between" spacing={1}>
            <Grid item>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h4">{simpleBatchTaskLabel || 'Slides and jobs'}</Typography>
                {slideRows.length > 0 &&
                  (isFetchingChildJobs ? (
                    <Skeleton variant="text" width={132} />
                  ) : isErrorChildJobs ? (
                    <Typography variant="body2" color="text.secondary">
                      {manifestSlideIds.length} slides · Progress unavailable
                    </Typography>
                  ) : completedSlideCount === manifestSlideIds.length ? (
                    <Typography variant="body2" color="text.secondary">
                      {manifestSlideIds.length} slides · All complete
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {manifestSlideIds.length} slides · {completedSlideCount} complete
                    </Typography>
                  ))}
              </Stack>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {!isEmpty(failedSlideIds) && job.studyId && (
                  <Link
                    to={getUrlToDatasetTabForSlideIds({
                      studyId: job.studyId,
                      labId,
                      slideIds: failedSlideIds,
                      statuses: [JobStatus.Failed],
                    })}
                    component={RouterLink}
                    underline="hover"
                  >
                    View {failedSlideIds.length} failed slide{failedSlideIds.length !== 1 ? 's' : ''} in Dataset
                  </Link>
                )}
                {!isEmpty(failedSlideIds) && (
                  <Tooltip title={slideClipboard.copied ? 'Copied' : `Copy ${failedSlideIds.length} slide IDs`}>
                    <IconButton
                      size="small"
                      color="inherit"
                      aria-label="Copy failed slide IDs"
                      onClick={() => slideClipboard.copy(failedSlideIds.join('\n'))}
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
                {slideRows.length > 0 && (
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<DownloadIcon fontSize="small" />}
                    onClick={handleExportSlidesCsv}
                  >
                    Export CSV
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
          {slideRows.length > 0 && (
            <Grid item container alignItems="center" spacing={1.5}>
              <Grid item xs={12} sm={5} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Search case ID or slide ID"
                  value={slideSearch}
                  onChange={(event) => setSlideSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {availableStatuses.length > 0 && (
                <Grid item xs>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label="All"
                      size="small"
                      color={statusFilter ? 'default' : 'primary'}
                      variant={statusFilter ? 'outlined' : 'filled'}
                      aria-label="Filter status: All"
                      aria-pressed={!statusFilter}
                      onClick={() => setStatusFilter(undefined)}
                    />
                    {map(availableStatuses, (status) => {
                      const selected = statusFilter === status;
                      const label = getStatusLabel(status);
                      return (
                        <Chip
                          key={status}
                          label={label}
                          size="small"
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          aria-label={`Filter status: ${label}`}
                          aria-pressed={selected}
                          onClick={() => setStatusFilter(selected ? undefined : status)}
                        />
                      );
                    })}
                  </Stack>
                </Grid>
              )}
            </Grid>
          )}
          {visibleSlideIds.length > COLLAPSE_SLIDES_ABOVE ? (
            <Grid item>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="job-slides-content"
                  id="job-slides-header"
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography variant="h4">Slides</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails id="job-slides-content">
                  <Grid container direction="column" spacing={1}>
                    {map(visibleSlideRows, renderSlideRow)}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ) : (
            map(visibleSlideRows, renderSlideRow)
          )}
          {isWaitingForCaseSearch && <Skeleton variant="text" width="70%" aria-label="Searching case details" />}
          {isSlideDetailsError && normalizedSlideSearch && !searchMatchesSlideId && (
            <Grid item>
              <Typography variant="body2" color="error">
                Case details could not be loaded. Search by slide ID instead.
              </Typography>
            </Grid>
          )}
          {normalizedSlideSearch &&
            !isWaitingForCaseSearch &&
            !isSlideDetailsError &&
            visibleSlideRows.length === 0 &&
            visibleChildJobsWithoutSlide.length === 0 && (
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  No slides match these filters.
                </Typography>
              </Grid>
            )}
          {visibleChildJobsWithoutSlide.length > 0 && (
            <Grid item container direction="column" spacing={0.5}>
              <Grid item>
                <Typography variant="subtitle2">
                  {job.type === JobType.PrepareCustomerResults ? 'Export shards' : 'Other child jobs'}
                </Typography>
              </Grid>
              {map(visibleChildJobsWithoutSlide, (childJob) => (
                <SubtaskJobRow
                  key={childJob.id || 'unmapped-job'}
                  job={childJob}
                  parentJobId={job.id}
                  parentJobType={job.type}
                  parentJobName={job.name}
                  isLoading={isFetchingChildJobs}
                  statusLabel={getStatusLabel}
                  hideJobLabel={Boolean(simpleBatchTaskLabel)}
                />
              ))}
            </Grid>
          )}
          {slideRows.length === 0 && hydratedChildJobs.length === 0 && !isFetchingChildJobs && (
            <Grid item>
              <Typography variant="body2" color="text.secondary">
                No slide manifest or child jobs are recorded for this job.
              </Typography>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
